import React from 'react';
import { Redirect } from 'react-router';

import {
  TClientsTableSortableField,
  TClientsTableItem,
  PageSize,
} from 'types/state';
import { ClientsTable } from 'containers/ClientsTable';
import { useServices } from 'hooks/useServices';
import { useIntlDictionary } from 'hooks/useIntlDictionary';
import { ClientDetails } from 'containers/ClientDetails';
import { useClientsPageMode } from 'hooks/useClientsPageMode';
import { useServiceState } from 'hooks/useServiceState';
import { routes } from 'utils/routes';
import { Pagination } from 'design/pagination';
import { testIdMap } from 'utils/testIdMap';
import { AdminContentLayout } from 'design/adminContentLayout';
import { AdminPageTitleLayout } from 'design/adminPageTitleLayout';
import { AdminPageHeadLayout } from 'design/adminPageHeadLayout';
import { PageSizeControl } from 'containers/PageSizeControl';
import { useTableSortingController } from 'hooks/useTableSortingController';
import { TableControlsLayout } from 'design/tableControlsLayout';
import { useAppState } from 'hooks/useAppState';
import { CardPaymentDetails } from 'containers/CardPaymentDetails';
import { GiroPaymentManager } from 'containers/GiroPaymentManager/GiroPaymentManager';
import { TextComponent } from 'design/text/textComponents';
import { SearchInputLayoutComponent } from 'design/searchInputLayout';
import { TextInputControlComponent } from 'design/textInputControl';
import { AdminNotificationPanel } from 'containers/AdminNotificationPanel';

type Props = {
  currentPage: number;
};

const DEFAULT_SORT_FIELD: TClientsTableSortableField = 'updatedOn';
const DEFAULT_SORT_ASCENDING_FLAG = false;
const MIN_SEARCH_VALUE_LENGTH_TO_SEARCH_BY = 2;

export const ClientsPageContent = (props: Props) => {
  const { currentPage } = props;

  const {
    cardPaymentManager: { cardPaymentId },
    giroPaymentManager: { giroPaymentId },
  } = useAppState();

  const mode = useClientsPageMode();

  const { sortBy, tableSorting: sorting } = useTableSortingController({
    initialField: DEFAULT_SORT_FIELD,
    initialAscending: DEFAULT_SORT_ASCENDING_FLAG,
  });

  // Подключение интернационализации.
  const intl = useIntlDictionary();

  // Флаг отвечает за появление и скрытие модального окна
  // с подробностями о клиенте.
  const [
    clientIdToShowDetailsFor,
    setClientIdToShowDetailsFor,
  ] = React.useState<null | number>(null);

  // Состояние хранит количество страниц, доступных для
  // загрузки. Состояние хранится отдельно, чтобы пагинация
  // не пропадала при каждом новом вызове сервиса.
  const [totalPagesCount, setTotalPagesCount] = React.useState<null | number>(
    null
  );

  // Состояние хранит текстовое значение по которому будет
  // производиться поиск клиентов.
  const [searchValue, setSearchValue] = React.useState('');

  // Для осуществления поиска по текстовому значению подходит
  // только строка определенной длины. Чтобы не провоцоировать
  // лишний вызов сервиса на получение данных страницы, значения
  // поисковой строки меньше определенной длины приводятся к
  // одинаковому значению.
  const searchBy =
    searchValue.length < MIN_SEARCH_VALUE_LENGTH_TO_SEARCH_BY
      ? null
      : searchValue;

  // Обработчик изменившегося значения поисковой строки.
  const onSearchValueChange = setSearchValue;

  // Состояние хранит значение размера страницы.
  const [sizeValue, setSizeValue] = React.useState<PageSize>(20);

  // Обработчик изменившегося значения размера страницы.
  const onSizeValueChange = setSizeValue;

  // Подключение сервисов.
  const { getClientsPageData } = useServices();

  // Состояние и метод вызова сервиса на получение данных страницы.
  const [pageDataState, getPageDataByParams] = useServiceState(
    getClientsPageData
  );

  const getPageData = React.useCallback(() => {
    const params = { page: currentPage, pageSize: sizeValue, mode, sorting };
    if (searchBy) {
      getPageDataByParams({ ...params, searchBy });
    } else {
      getPageDataByParams(params);
    }
  }, [currentPage, getPageDataByParams, mode, searchBy, sizeValue, sorting]);

  // Эффект вызывает сервис на получение данных страницы после
  // монтирования и каждый раз, когда изменится один из параметров.
  // Используется useLayoutEfeect чтобы избежать мерцания индикатора
  // сортировки.
  React.useLayoutEffect(() => {
    getPageData();
  }, [getPageData]);

  // Эффект следит за состоянием сервиса на получение данных страницы
  // и обновляет значение доступного количества страниц.
  // Используется useLayoutEffect чтобы в случае несуществующей
  // страницы осуществить редирект без отрисовки пустых данных.
  React.useLayoutEffect(() => {
    if (pageDataState && pageDataState.resolvedWith) {
      setTotalPagesCount(pageDataState.resolvedWith.totalPagesCount);
    }
  }, [pageDataState]);

  // Список клиентов кэшируется в локальном состоянии.
  const [tableItems, setTableItems] = React.useState<
    null | TClientsTableItem[]
  >(null);

  // Кэшированный список клиентов инвалидируется при смене страницы.
  React.useEffect(() => {
    setTableItems(null);
  }, []);

  // При получении нового списка клиентов он заменяет собой кэшированный список.
  React.useEffect(() => {
    const tableItems =
      pageDataState &&
      pageDataState.resolvedWith &&
      pageDataState.resolvedWith.clients;

    if (tableItems) {
      setTableItems(tableItems);
    }
  }, [pageDataState]);

  // Обработчик закрытия подробностей о клиенте.
  // Скрывает подробности о клиенте.
  const onClientDetailsClose = React.useCallback(() => {
    setClientIdToShowDetailsFor(null);
  }, []);

  // Обработчик нажатия по строке таблицы.
  // Показывает подробности о клиенте.
  const handleTableItemClick = React.useCallback(
    (id: number) => {
      setClientIdToShowDetailsFor(id);
      getPageData();
    },
    [setClientIdToShowDetailsFor, getPageData]
  );

  // Обработчик успешного редактирования логина клиента.
  // Вызывает обновление табличных данных.
  const handleClientLoginEdited = React.useCallback(() => {
    getPageData();
  }, [getPageData]);

  // Обработчик успешного редактирования профиля клиента.
  // Вызывает обновление табличных данных.
  const handleClientProfieEdited = React.useCallback(() => {
    getPageData();
  }, [getPageData]);

  // Обработчик успешного редактирования пароля клиента.
  // Вызывает обновление табличных данных (чтобы пользователь сместился в таблице вверх).
  const handleClientPasswordEdited = React.useCallback(() => {
    getPageData();
  }, [getPageData]);

  // Обработчик успешной смены статуса клиента.
  // Скрывает подробности о клиенте.
  // Вызывает обновление табличных данных (чтобы пользователь исчез из открытой таблицы).
  const handleClientStatusEdited = React.useCallback(() => {
    setClientIdToShowDetailsFor(null);
    getPageData();
  }, [getPageData]);

  // В случае, когда выбранная страница не существует,
  // осуществляется редирект на последнюю доступную страницу.
  // Ситуация возникает, например, когда переключается размер страницы.
  if (totalPagesCount !== null && currentPage > totalPagesCount) {
    if (totalPagesCount > 0) {
      if (mode === 'ACTIVE_CLIENTS') {
        return <Redirect to={routes.clients(totalPagesCount)} />;
      }
      if (mode === 'ARCHIVED_CLIENTS') {
        return <Redirect to={routes.archive(totalPagesCount)} />;
      }
    } else if (currentPage > 1) {
      if (mode === 'ACTIVE_CLIENTS') {
        return <Redirect to={routes.clients(1)} />;
      }
      if (mode === 'ARCHIVED_CLIENTS') {
        return <Redirect to={routes.archive(1)} />;
      }
    }
  }

  const paginationBasePath = (() => {
    switch (mode) {
      case 'ACTIVE_CLIENTS':
        return routes.clientsRoot();
      case 'ARCHIVED_CLIENTS':
        return routes.archiveRoot();
      default:
        throw new TypeError();
    }
  })();

  return (
    <AdminContentLayout
      headNode={
        <AdminPageHeadLayout>
          <AdminNotificationPanel />
          <AdminPageTitleLayout
            textNode={
              <TextComponent size="mp">
                {intl.adminNavigation.actualClientsLinkText}
              </TextComponent>
            }
            actionNode={null}
          />
          <TableControlsLayout
            primaryControlsNode={
              <TextComponent size="s">
                <SearchInputLayoutComponent
                  control={
                    <TextInputControlComponent
                      placeholder={intl.clientsPageSearchPlaceholder}
                      value={searchValue}
                      onValueChange={onSearchValueChange}
                      testId={testIdMap.CLIENTS_PAGE__SEARCH}
                    />
                  }
                />
              </TextComponent>
            }
            secondaryControlsNode={
              <>
                {totalPagesCount !== null && (
                  <TextComponent size="s">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPagesCount}
                      basePath={paginationBasePath}
                    />
                  </TextComponent>
                )}
                <TextComponent size="s">
                  <PageSizeControl
                    value={sizeValue}
                    onValueChange={onSizeValueChange}
                  />
                </TextComponent>
              </>
            }
          ></TableControlsLayout>
        </AdminPageHeadLayout>
      }
      bodyNode={
        <>
          {tableItems !== null && (
            <ClientsTable
              items={tableItems}
              activeItemId={clientIdToShowDetailsFor || undefined}
              sortedBy={sorting.field}
              ascendingSort={sorting.ascending}
              onSortChange={sortBy}
              onItemClick={handleTableItemClick}
            />
          )}
          {clientIdToShowDetailsFor !== null && (
            <ClientDetails
              clientId={clientIdToShowDetailsFor}
              onClose={onClientDetailsClose}
              onLoginEdited={handleClientLoginEdited}
              onPasswordEdited={handleClientPasswordEdited}
              onProfileEdited={handleClientProfieEdited}
              onStatusEdited={handleClientStatusEdited}
              onDebitPayed={handleClientStatusEdited}
            />
          )}
          {cardPaymentId !== null && (
            <CardPaymentDetails
              cardPaymentId={cardPaymentId}
              onShowClientDetails={setClientIdToShowDetailsFor}
            />
          )}
          {giroPaymentId !== null && (
            <GiroPaymentManager
              giroPaymentId={giroPaymentId}
              onViewClientDetails={setClientIdToShowDetailsFor}
              onPaymentCancelled={() => {}}
              onPaymentConfirmed={() => {}}
              onClientEdited={() => {}}
              onRateEdited={() => {}}
            />
          )}
        </>
      }
    />
  );
};
